import { useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  type Export,
  type ExportError,
  type ExportRequest,
  TERMINAL_STATUSES,
  isExportExpiredError,
  isKnownExportError,
} from '@/types/export';
import { useCreateExportMutation } from '@/hooks/data/export/useCreateExportMutation';
import { useExportStore } from '@/hooks/data/export/useExportStore';
import { STALE, useLatestAsync } from '@/hooks/useLatestAsync';
import { getExport } from '@/services/export.service';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { download } from '@/lib/download';
import { useTranslations } from 'next-intl';

export function useExportDownload() {
  // ── dry run ──────────────────────────────────────────────────────────────────

  const tCommon = useTranslations('common');
  const tDownloadZip = useTranslations('dialogs.downloadZip');
  const { toast } = useToast();

  const [dryRunExport, setDryRunExport] = useState<Export | null>(null);
  const [dryRunError, setDryRunError] = useState<ExportError | null>(null);

  // useLatestAsync ensures only the response to the most recently fired dry-run
  // request updates state — stale responses from earlier in-flight requests are
  // discarded. Uses mutateAsync (not mutate + per-call callbacks) because
  // per-call callbacks are dropped when the component unmounts, which React
  // Strict Mode triggers on every mount.
  const withLatestDryRun = useLatestAsync<Export>();
  const { mutateAsync: dryRunMutateAsync } = useCreateExportMutation();

  const createDryRunExport = useCallback(
    async (request: ExportRequest) => {
      setDryRunExport(null);
      try {
        const result = await withLatestDryRun(dryRunMutateAsync(request));
        if (result === STALE) {
          return;
        }
        setDryRunError(null);
        setDryRunExport(result);
      } catch (error: any) {
        if (isKnownExportError(error)) {
          setDryRunError(error);
        } else {
          setDryRunError({
            code: 'unknown',
            message: error.message,
            status: error.status ?? 0,
            assetsCount: 0,
            estimatedSizeBytes: 0,
          });
          toast({
            variant: ToastVariant.DESTRUCTIVE,
            title: tCommon('error'),
            description: tDownloadZip('summaryErrorDescription'),
          });
        }
      }
    },
    [dryRunMutateAsync, withLatestDryRun, tCommon, tDownloadZip, toast],
  );

  // ── real export ──────────────────────────────────────────────────────────────

  const tExport = useTranslations('toasts.export');
  const addExport = useExportStore((state) => state.addExport);

  // Same mutateAsync pattern as dry-run: the dialog closes immediately after
  // createExport is called, so per-call callbacks would be dropped. mutateAsync
  // resolves after unmount, and label is captured in the closure of each call
  // so a shared ref is no longer needed.
  const withLatestRealExport = useLatestAsync<Export>();
  const { mutateAsync: createExportMutateAsync, isPending: downloadIsPending } = useCreateExportMutation();

  const createExport = useCallback(
    async (request: ExportRequest, label: string) => {
      try {
        const result = await withLatestRealExport(createExportMutateAsync(request));
        if (result === STALE) {
          return;
        }
        if (result.status === 'nothing_to_export') {
          toast({ title: tExport('nothingToExport.title'), description: tExport('nothingToExport.description') });
        } else if (result.status === 'failed') {
          toast({
            variant: ToastVariant.DESTRUCTIVE,
            title: tExport('failed.title'),
            description: tExport('failed.description'),
          });
        } else if (!TERMINAL_STATUSES.includes(result.status)) {
          addExport(result.id, label);
        }
      } catch (error: any) {
        if (error.status === 422) {
          toast({ title: tExport('nothingToExport.title'), description: tExport('nothingToExport.description') });
        } else {
          toast({
            variant: ToastVariant.DESTRUCTIVE,
            title: tExport('failed.title'),
            description: tExport('failed.description'),
          });
        }
      }
    },
    [createExportMutateAsync, withLatestRealExport, tExport, toast, addExport],
  );

  // ── download ─────────────────────────────────────────────────────────────────

  const tErrors = useTranslations('errors');
  const { getAccessTokenSilently } = useAuth0();
  const [isExpired, setIsExpired] = useState(false);

  const downloadExport = async (exportId: string): Promise<void> => {
    try {
      const token = await getAccessTokenSilently();
      const exportData = await getExport({ id: exportId }, token);
      const filename = exportData.name ? `${exportData.name}.zip` : `export-${exportId}.zip`;
      await download(exportData.links.download, filename, token);
    } catch (error) {
      if (isExportExpiredError(error)) {
        setIsExpired(true);
        toast({
          variant: ToastVariant.DESTRUCTIVE,
          title: tCommon('error'),
          description: tErrors('exportExpired'),
        });
        return;
      }
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: tErrors('toastDefaultDescription'),
      });
    }
  };

  return {
    createDryRunExport,
    createExport,
    downloadIsPending,
    dryRunExport,
    dryRunError,
    downloadExport,
    isExpired,
  };
}
